import QRCode from "qrcode";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { WallItem, WallSnapshot } from "../../shared/types";
import { getWall } from "../api";
import { selectWallItems, type WallScreenName } from "../wallSelection";
import { subscribeVentuzFrames, useVentuzFrameClock, wallSyncSeed } from "../ventuzFrameClock";

const publicUrl = "https://madonna-outernet.lookport.live";
const backgroundLoopSeconds = 48;
const ceilingCardSetDurationMs = 30 * 60 * 1_000;
export const wallCardIntroDelayMs = 7_000;
const backgroundFrames = {
  blue: Array.from({ length: 6 }, (_, index) => `/media/bg-frames/blue-${index + 1}.webp`),
};
const strobeColors = ["#FFFFFF", "#FF52EC", "#18A9F5"] as const;

const cardColorPairs = [
  { background: "#DB1200", ink: "#000000" },
  { background: "#FF52EC", ink: "#000000" },
  { background: "#000000", ink: "#FF52EC" },
  { background: "#FFFFFF", ink: "#000000" },
] as const;

const screenSpecs = {
  east: { width: 3680, height: 3280, itemCount: 3 },
  south: { width: 8480, height: 4800, itemCount: 10 },
  west: { width: 6476, height: 3280, itemCount: 5 },
  north: { width: 6586, height: 3280, itemCount: 5 },
  ceiling: { width: 8492, height: 6094, itemCount: 8 },
} as const;

type ScreenName = keyof typeof screenSpecs;
type CollageSlot = {
  x: number;
  y: number;
  width: number;
  tilt: number;
};

type LiveWallCard = {
  item: WallItem;
  slotIndex: number;
  placementIndex: number;
  generation: number;
  phase: "entering" | "visible" | "exiting";
};

function ScreenStrobe({ screen, syncFps, syncEnabled }: {
  screen: ScreenName;
  syncFrame: number;
  syncFps: number;
  syncEnabled: boolean;
}) {
  const [color, setColor] = useState<string | null>(null);

  useEffect(() => {
    if (!syncEnabled) return;
    let lastColor: string | null = null;
    return subscribeVentuzFrames((frame) => {
      // A sparse, single-frame pop-art camera flash. Keeping one possible
      // accent in a long deterministic window prevents the installation from
      // reading as a broken/glitching render while preserving fleet sync.
      const bucketFrames = Math.max(1, Math.round(syncFps * 24));
      const bucket = Math.floor(frame / bucketFrames);
      const localFrame = frame % bucketFrames;
      const seed = hashString(`${wallSyncSeed}:${screen}:strobe:${bucket}`);
      // Some windows intentionally contain no flash, producing an average
      // interval of roughly 35 seconds rather than a repetitive pulse.
      const enabledThisBucket = ((seed >>> 9) % 100) < 68;
      const first = Math.round(syncFps * (4 + (seed % 1_500) / 100));
      // Hold for several installation frames. A one-frame React state change
      // could be batched away entirely by Chromium/Ventuz, which made the
      // flash visible only by accident during the ceiling's own fade-out.
      const flashFrames = Math.max(4, Math.round(syncFps * .1));
      const active = enabledThisBucket && localFrame >= first && localFrame < first + flashFrames;
      const nextColor = active ? strobeColors[(seed >>> 18) % strobeColors.length] : null;
      if (nextColor !== lastColor) {
        lastColor = nextColor;
        setColor(nextColor);
      }
    });
  }, [screen, syncEnabled, syncFps]);

  useEffect(() => {
    if (syncEnabled) return;
    let cancelled = false;
    const timers = new Set<number>();

    const later = (callback: () => void, delay: number) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (!cancelled) callback();
      }, delay);
      timers.add(timer);
    };

    const scheduleBurst = () => {
      setColor(strobeColors[Math.floor(Math.random() * strobeColors.length)]);
      // Long enough to survive browser/LED frame scheduling, still short
      // enough to read as a camera flash rather than a colour scene.
      later(() => setColor(null), 100);
      later(scheduleBurst, 22_000 + Math.random() * 24_000);
    };

    later(scheduleBurst, 12_000 + Math.random() * 16_000);
    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [syncEnabled]);

  const activeColor = color;

  return (
    <div
      className={`wall-strobe${activeColor ? " wall-strobe--active" : ""}`}
      style={{ "--wall-strobe-color": activeColor ?? "transparent" } as CSSProperties}
      aria-hidden="true"
    />
  );
}

type TunnelSlot = {
  plane: "left" | "right" | "top" | "bottom";
  depthIndex: number;
  order: number;
  x: number;
  y: number;
  angle: number;
  width: number;
  height: number;
  transform: string;
  opacity: number;
  points: readonly [Point, Point, Point, Point];
};

const collageSlots: Record<ScreenName, readonly CollageSlot[]> = {
  east: [
    { x: 3.5, y: 7, width: 12, tilt: -2.1 },
    { x: 76.5, y: 9, width: 11.8, tilt: 1.7 },
    { x: 40, y: 60, width: 12.2, tilt: -.8 },
  ],
  south: [
    { x: 5, y: 7, width: 6.5, tilt: -1.8 },
    { x: 22, y: 9, width: 6.2, tilt: 1.15 },
    { x: 41, y: 5, width: 6.4, tilt: -1.25 },
    { x: 61.5, y: 9, width: 6.2, tilt: 1.45 },
    { x: 83.5, y: 7, width: 6.5, tilt: -1.05 },
    { x: 7, y: 27, width: 6.1, tilt: 1.1 },
    { x: 25, y: 29, width: 6, tilt: -1.35 },
    { x: 43.5, y: 25, width: 6.3, tilt: .75 },
    { x: 62.5, y: 29, width: 6, tilt: 1.25 },
    { x: 82.5, y: 27, width: 6.1, tilt: -.95 },
    { x: 3.5, y: 47, width: 5.8, tilt: -.9 },
    { x: 86, y: 46, width: 5.8, tilt: 1.05 },
    { x: 17, y: 71, width: 5.7, tilt: 1.15 },
    { x: 35.5, y: 73, width: 5.6, tilt: -.8 },
    { x: 57, y: 72, width: 5.6, tilt: 1.05 },
    { x: 76, y: 71, width: 5.7, tilt: -1.2 },
    { x: 21, y: 47, width: 5.7, tilt: .65 },
    { x: 70, y: 46, width: 5.7, tilt: -.7 },
  ],
  west: [
    { x: 3, y: 5, width: 10.8, tilt: -1.9 },
    { x: 78, y: 5, width: 10.7, tilt: 1.4 },
    { x: 3, y: 55, width: 10.7, tilt: 1.5 },
    { x: 78, y: 55, width: 10.8, tilt: -1.3 },
    { x: 46, y: 68, width: 7.5, tilt: -.7 },
  ],
  north: [
    { x: 4, y: 4, width: 11, tilt: 1.8 },
    { x: 43, y: 4, width: 11.4, tilt: -1.5 },
    { x: 85, y: 6, width: 10.6, tilt: 1.1 },
    { x: 23, y: 50, width: 11.3, tilt: -.9 },
    { x: 64, y: 50, width: 11, tilt: 1.7 },
  ],
  ceiling: [
    { x: 3, y: 63, width: 23, tilt: -2.2 },
    { x: 73, y: 61, width: 23, tilt: 2.1 },
    { x: 15, y: 43, width: 18, tilt: -1.7 },
    { x: 67, y: 41, width: 18, tilt: 1.6 },
    { x: 27, y: 27, width: 14, tilt: -1.1 },
    { x: 59, y: 26, width: 14, tilt: 1.1 },
    { x: 39, y: 14, width: 9, tilt: -.55 },
    { x: 53, y: 14, width: 9, tilt: .55 },
  ],
};

// A tiny approved pool needs an intentionally composed hero layout. Scaling
// the regular 5–10 card grid left two UI-sized islands on an 8K canvas.
type Point = { x: number; y: number };

const wallTickerUnit = "C’MON MEET ME ON THE DANCEFLOOR · MADONNA";

export const wallTickerText = (screen: ScreenName): string =>
  screen === "east" ? wallTickerUnit : `${wallTickerUnit} · ${wallTickerUnit} ·`;

export const ceilingSegmentBounds = (
  plane: TunnelSlot["plane"],
  segments: number,
  order: number,
): readonly [number, number] => {
  const isHorizontalPlane = plane === "top" || plane === "bottom";
  const sideGap = isHorizontalPlane ? .006 : .015;
  if (!isHorizontalPlane) {
    return [(order + sideGap) / segments, (order + 1 - sideGap) / segments];
  }

  // Keep every card at its approved width, but compress the column centres
  // into the middle 90% of the horizontal walls. The outer columns therefore
  // step away from the side screens while all intermediate gaps shrink by the
  // same amount instead of leaving irregular black channels.
  const edgeInset = .05;
  const center = edgeInset + (order + .5) * (1 - edgeInset * 2) / segments;
  const halfWidth = (1 - sideGap * 2) / (segments * 2);
  return [Math.max(0, center - halfWidth), Math.min(1, center + halfWidth)];
};

export const ceilingOpacityAtDepth = (depthProgress: number): number => {
  const fadeProgress = Math.max(0, Math.min(1, depthProgress));
  const easedFade = fadeProgress ** 1.25;
  return .035 + .965 * (1 - easedFade);
};

const smoothStep = (progress: number) => {
  const clamped = Math.max(0, Math.min(1, progress));
  return clamped * clamped * (3 - 2 * clamped);
};

export const ceilingLevelOpacityAtTime = (
  elapsedSeconds: number,
  depthIndex: number,
  levelCount: number,
): number => {
  const duration = 36;
  const phase = ((elapsedSeconds % duration) + duration) % duration;
  const safeLevelCount = Math.max(1, levelCount);
  const level = Math.max(0, Math.min(safeLevelCount - 1, depthIndex));
  const levelProgress = safeLevelCount === 1 ? 0 : level / (safeLevelCount - 1);

  // Build from the physical outer edge towards the aperture. Each belt owns
  // a distinct 0.78s dissolve, staggered across 4.7 seconds in total.
  const enterStart = .15 + levelProgress * 3.75;
  const enterOpacity = smoothStep((phase - enterStart) / .78);

  // The exit follows the same approved direction as the entrance: the outer
  // belt disappears first and the wipe continues towards the aperture.
  const exitStart = 28.8 + levelProgress * 4.15;
  const exitOpacity = 1 - smoothStep((phase - exitStart) / .72);

  return Math.min(enterOpacity, exitOpacity);
};

export const ceilingAnimationSecondsSinceIntro = (wallElapsedMs: number): number =>
  Math.max(0, wallElapsedMs - wallCardIntroDelayMs) / 1_000;

const interpolatePoint = (from: Point, to: Point, amount: number): Point => ({
  x: from.x + (to.x - from.x) * amount,
  y: from.y + (to.y - from.y) * amount,
});

const perspectiveMatrix = (
  topLeft: Point,
  topRight: Point,
  bottomRight: Point,
  bottomLeft: Point,
  sourceWidth: number,
  sourceHeight: number,
): string => {
  const dx1 = topRight.x - bottomRight.x;
  const dx2 = bottomLeft.x - bottomRight.x;
  const dx3 = topLeft.x - topRight.x + bottomRight.x - bottomLeft.x;
  const dy1 = topRight.y - bottomRight.y;
  const dy2 = bottomLeft.y - bottomRight.y;
  const dy3 = topLeft.y - topRight.y + bottomRight.y - bottomLeft.y;
  const denominator = dx1 * dy2 - dx2 * dy1;
  const g = Math.abs(denominator) < .000001 ? 0 : (dx3 * dy2 - dx2 * dy3) / denominator;
  const h = Math.abs(denominator) < .000001 ? 0 : (dx1 * dy3 - dx3 * dy1) / denominator;
  const a = topRight.x - topLeft.x + g * topRight.x;
  const b = bottomLeft.x - topLeft.x + h * bottomLeft.x;
  const c = topLeft.x;
  const d = topRight.y - topLeft.y + g * topRight.y;
  const e = bottomLeft.y - topLeft.y + h * bottomLeft.y;
  const f = topLeft.y;
  const values = [
    a / sourceWidth, d / sourceWidth, 0, g / sourceWidth,
    b / sourceHeight, e / sourceHeight, 0, h / sourceHeight,
    0, 0, 1, 0,
    c, f, 0, 1,
  ];
  return `matrix3d(${values.map((value) => Number(value.toFixed(9))).join(",")})`;
};

export const buildCeilingTunnelSlots = (width: number, height: number): readonly TunnelSlot[] => {
  if (width <= 0 || height <= 0) return [];

  // Exact responsive projection of the supplied ceiling UV polygon.
  const a = { x: 0, y: height * .39 };
  const b = { x: width * .73, y: 0 };
  const c = { x: width, y: height };
  const d = { x: 0, y: height };
  // Keep the outer UV polygon fixed, but aim the perspective into the visual
  // centre of the irregular ceiling shape rather than the canvas midpoint.
  const vanishingPoint = { x: width * .44, y: height * .605 };
  // Preserve the approved perspective cadence and the open central aperture.
  // Portraits that need more height reserve several of these original belts
  // at layout time; changing this ratio itself collapses the aperture and
  // pushes the near rows much too far apart.
  // Open the central aperture a little further and tighten the belt cadence.
  // This makes the ceiling planes read flatter while reducing the heavy black
  // bands between neighbouring rows.
  const ringScale = .918;
  const depths = Array.from({ length: 14 }, (_, index) => 1 - ringScale ** index);
  const lastDepthIndex = depths.length - 2;
  const sourceWidth = 1000;
  const sourceHeight = 1250;
  const sideLength = (from: Point, to: Point) => Math.hypot(to.x - from.x, to.y - from.y);
  const targetSegmentLength = sideLength(a, d) / 3;
  const reducedSegmentCount = (from: Point, to: Point) => Math.max(
    2,
    Math.round(sideLength(from, to) / targetSegmentLength) - 1,
  );
  const sides = [
    { plane: "left" as const, from: a, to: d, segments: 3 },
    // Keep the broad horizontal planes editorially composed instead of
    // allowing responsive length heuristics to add narrow columns.
    { plane: "top" as const, from: b, to: a, segments: 5 },
    { plane: "right" as const, from: c, to: b, segments: reducedSegmentCount(c, b) },
    { plane: "bottom" as const, from: d, to: c, segments: 7 },
  ];
  return depths.slice(0, -1).flatMap((outerDepth, depthIndex) => sides.flatMap(({ plane, from, to, segments }) => {
    const innerDepth = depths[depthIndex + 1];
    // Preserve the approved portal projection, but inset every card within its
    // ring segment. Edge-to-edge faces read as a checkerboard; these gutters
    // make the exact same geometry read as separate editorial cards.
    return Array.from({ length: segments }, (_, order) => {
      // Every ring follows the exact UV edges. Uniform gutters keep all four
      // planes aligned instead of letting the top/right walls drift.
      const depthGap = .065;
      const cardOuterDepth = outerDepth + (innerDepth - outerDepth) * depthGap;
      const cardInnerDepth = innerDepth - (innerDepth - outerDepth) * depthGap;
      const outerStart = interpolatePoint(from, vanishingPoint, cardOuterDepth);
      const outerEnd = interpolatePoint(to, vanishingPoint, cardOuterDepth);
      const innerStart = interpolatePoint(from, vanishingPoint, cardInnerDepth);
      const innerEnd = interpolatePoint(to, vanishingPoint, cardInnerDepth);
      const angle = Math.atan2(outerEnd.y - outerStart.y, outerEnd.x - outerStart.x) * 180 / Math.PI;
      // Keep neighbouring cards visually close across each wall.  The former
      // 5.5% inset was multiplied by the already-wide ceiling segments and
      // produced the large comb-like horizontal gaps visible on the venue.
      const [sampleStart, sampleEnd] = ceilingSegmentBounds(plane, segments, order);
      const outerSampleStart = interpolatePoint(outerStart, outerEnd, sampleStart);
      const outerSampleEnd = interpolatePoint(outerStart, outerEnd, sampleEnd);
      const innerSampleStart = interpolatePoint(innerStart, innerEnd, sampleStart);
      const innerSampleEnd = interpolatePoint(innerStart, innerEnd, sampleEnd);
      // The lower edge of every source card faces the UV boundary it came from.
      // Mapping the complete card (rather than clipping a flat rectangle) keeps
      // portraits and typography aligned with the four perspective planes.
      const points = [innerSampleStart, innerSampleEnd, outerSampleEnd, outerSampleStart];
      const left = Math.min(...points.map((point) => point.x));
      const top = Math.min(...points.map((point) => point.y));
      const right = Math.max(...points.map((point) => point.x));
      const bottom = Math.max(...points.map((point) => point.y));
      const cardWidth = Math.max(right - left, 1);
      const cardHeight = Math.max(bottom - top, 1);
      const depthProgress = depthIndex / Math.max(1, lastDepthIndex);
      return {
        plane,
        depthIndex,
        order,
        x: left,
        y: top,
        angle,
        width: cardWidth,
        height: cardHeight,
        transform: perspectiveMatrix(points[0], points[1], points[2], points[3], sourceWidth, sourceHeight),
        // Start fading from the first inner belt. The gentle power curve keeps
        // the foreground legible but makes every successive row visibly softer
        // instead of concentrating the entire dissolve in the final two rows.
        opacity: ceilingOpacityAtDepth(depthProgress),
        points: [points[0], points[1], points[2], points[3]] as const,
      };
    });
  }));
};

const loadCanvasImage = (src: string): Promise<HTMLImageElement | null> => new Promise((resolve) => {
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.onload = () => resolve(image);
  image.onerror = () => resolve(null);
  image.src = src;
});

const fitCanvasText = (
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] => {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (line && context.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const clipped = lines.slice(0, maxLines);
    clipped[maxLines - 1] = `${clipped[maxLines - 1].replace(/[.,;:!?]?$/, "")}…`;
    return clipped;
  }
  return lines;
};

const buildCeilingCardCanvas = async (
  item: WallItem,
  colorIndex: number,
  textureScale: number,
): Promise<HTMLCanvasElement> => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(1000 * textureScale);
  canvas.height = Math.round(1250 * textureScale);
  const context = canvas.getContext("2d")!;
  context.scale(textureScale, textureScale);
  const pair = cardColorPairs[colorIndex];
  const altOffset = 1 + (hashString(`ceiling-alt:${item.id}`) % (cardColorPairs.length - 1));
  const alt = cardColorPairs[(colorIndex + altOffset) % cardColorPairs.length];
  context.clearRect(0, 0, canvas.width, canvas.height);

  const image = item.hasImage && item.imageUrl ? await loadCanvasImage(item.imageUrl) : null;
  const cardX = image ? 125 : 115;
  const cardWidth = image ? 750 : 770;
  let copyY = image ? 875 : 470;
  if (image) {
    // Match the 4:5 portrait frame used by the wall cards. The former 5:6
    // ceiling crop removed extra pixels from the top and bottom of portraits,
    // which made faces read as vertically compressed in perspective.
    const imageHeight = cardWidth * 5 / 4;
    const scale = Math.max(cardWidth / image.naturalWidth, imageHeight / image.naturalHeight);
    const sourceWidth = cardWidth / scale;
    const sourceHeight = imageHeight / scale;
    context.save();
    context.beginPath();
    context.rect(cardX, 60, cardWidth, imageHeight);
    context.clip();
    context.drawImage(
      image,
      (image.naturalWidth - sourceWidth) / 2,
      (image.naturalHeight - sourceHeight) / 2,
      sourceWidth,
      sourceHeight,
      cardX,
      60,
      cardWidth,
      imageHeight,
    );
    context.restore();
  }

  context.font = '900 104px "Helvetica Neue LT Pro 87 Heavy Condensed"';
  const nameWidth = Math.min(cardWidth * .72, Math.max(245, context.measureText(item.name.toUpperCase()).width + 76));
  const nameX = cardX + 42 + ((hashString(`ceiling-name:${item.id}`) % 90) - 45);
  const nameY = copyY - 86;
  context.fillStyle = pair.background;
  context.fillRect(nameX, nameY, nameWidth, 112);
  context.fillStyle = pair.ink;
  context.textBaseline = "middle";
  context.fillText(item.name.toUpperCase(), nameX + 34, nameY + 59, nameWidth - 68);

  const messageLength = item.message.length;
  const fontSize = messageLength > 120 ? 43 : messageLength > 72 ? 50 : 60;
  context.font = `700 ${fontSize}px "Helvetica Neue LT Pro 75 Bold"`;
  const lines = fitCanvasText(context, item.message, cardWidth - 70, image ? 4 : 5);
  const lineHeight = fontSize * 1.1;
  const copyHeight = Math.max(118, lines.length * lineHeight + 54);
  context.fillStyle = alt.background;
  context.fillRect(cardX, copyY, cardWidth, copyHeight);
  context.fillStyle = alt.ink;
  context.textBaseline = "top";
  lines.forEach((line, lineIndex) => context.fillText(line, cardX + 35, copyY + 27 + lineIndex * lineHeight));

  // The tunnel must project the actual editorial card, not the old 1000x1250
  // transparent artboard. Projecting that artboard made short text cards look
  // like thin, low-resolution strips and needlessly spent texture memory.
  const padding = 22;
  const cropLeft = Math.max(0, Math.min(cardX, nameX) - padding);
  const cropTop = Math.max(0, (image ? 60 : nameY) - padding);
  const cropRight = Math.min(1000, Math.max(cardX + cardWidth, nameX + nameWidth) + padding);
  const cropBottom = Math.min(1250, copyY + copyHeight + padding);
  const cropped = document.createElement("canvas");
  cropped.width = Math.max(1, Math.round((cropRight - cropLeft) * textureScale));
  cropped.height = Math.max(1, Math.round((cropBottom - cropTop) * textureScale));
  const croppedContext = cropped.getContext("2d")!;
  croppedContext.imageSmoothingEnabled = true;
  croppedContext.imageSmoothingQuality = "high";
  croppedContext.drawImage(
    canvas,
    Math.round(cropLeft * textureScale),
    Math.round(cropTop * textureScale),
    cropped.width,
    cropped.height,
    0,
    0,
    cropped.width,
    cropped.height,
  );
  return cropped;
};

export const fitCeilingTextureIntoSlot = (
  points: readonly [Point, Point, Point, Point],
  textureAspect: number,
  preservePortraitAspect = false,
  portraitWidthScale = .74,
  portraitWidthBoost = 1,
): readonly [Point, Point, Point, Point] => {
  const distance = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);
  const along = (distance(points[0], points[1]) + distance(points[3], points[2])) / 2;
  const across = (distance(points[0], points[3]) + distance(points[1], points[2])) / 2;
  const slotAspect = Math.max(.001, along / Math.max(across, .001));
  // Fit both dimensions from one aspect equation. Independent minimum clamps
  // made short copy cards look vertically stretched and portraits look
  // horizontally compressed. Portrait slots are merged across enough belts
  // before reaching this function, so they can stay broad without distortion.
  const maxUScale = preservePortraitAspect ? portraitWidthScale : .98;
  const maxVScale = preservePortraitAspect ? .92 : .86;
  let uScale = maxUScale;
  let vScale = slotAspect * uScale / Math.max(.001, textureAspect);
  if (vScale > maxVScale) {
    const correction = maxVScale / vScale;
    uScale *= correction;
    vScale = maxVScale;
  }
  // The top and bottom planes are seen through a much stronger physical
  // foreshortening than the side walls. Compensate horizontally after the
  // aspect fit so the portrait height and row cadence stay unchanged. Capping
  // the result inside its segment prevents neighbouring columns overlapping.
  if (preservePortraitAspect) uScale = Math.min(.98, uScale * portraitWidthBoost);
  // Preserve the approved horizontal fit, then shorten every photo card by
  // the same amount around its centre. This removes the remaining vertical
  // stretch while keeping symmetric, consistent breathing room between rows.
  if (preservePortraitAspect) vScale *= .94;
  const u0 = (1 - uScale) / 2;
  const u1 = 1 - u0;
  const v0 = (1 - vScale) / 2;
  const v1 = 1 - v0;
  const sample = (u: number, v: number) => {
    const near = interpolatePoint(points[0], points[1], u);
    const far = interpolatePoint(points[3], points[2], u);
    return interpolatePoint(near, far, v);
  };
  return [sample(u0, v0), sample(u1, v0), sample(u1, v1), sample(u0, v1)];
};

const scaleCeilingQuadAroundCenter = (
  points: readonly [Point, Point, Point, Point],
  scale: number,
): readonly [Point, Point, Point, Point] => {
  const center = points.reduce(
    (result, point) => ({ x: result.x + point.x / points.length, y: result.y + point.y / points.length }),
    { x: 0, y: 0 },
  );
  return points.map((point) => ({
    x: center.x + (point.x - center.x) * scale,
    y: center.y + (point.y - center.y) * scale,
  })) as unknown as readonly [Point, Point, Point, Point];
};

const ceilingVertexShader = `
  attribute vec2 a_position;
  attribute vec2 a_uv;
  varying vec2 v_uv;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_uv = a_uv;
  }
`;

const ceilingFragmentShader = `
  precision mediump float;
  uniform sampler2D u_texture;
  uniform float u_opacity;
  varying vec2 v_uv;
  void main() {
    gl_FragColor = texture2D(u_texture, v_uv) * u_opacity;
  }
`;

type CeilingWebGlScene = {
  renderAt: (elapsedSeconds: number) => void;
  dispose: () => void;
};

const emptyCeilingWebGlScene = (): CeilingWebGlScene => ({
  renderAt: () => {},
  dispose: () => {},
});

const compileCeilingShader = (gl: WebGLRenderingContext | WebGL2RenderingContext, type: number, source: string) => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

const drawCeilingWebGl = (
  canvas: HTMLCanvasElement,
  textures: Map<string, HTMLCanvasElement>,
  slots: readonly TunnelSlot[],
  items: readonly WallItem[],
  renderScaleX: number,
  renderScaleY: number,
): CeilingWebGlScene => {
  const contextOptions: WebGLContextAttributes = {
    alpha: true,
    antialias: true,
    premultipliedAlpha: true,
    preserveDrawingBuffer: true,
  };
  const gl = canvas.getContext("webgl2", contextOptions) ?? canvas.getContext("webgl", contextOptions);
  if (!gl) return emptyCeilingWebGlScene();
  const vertexShader = compileCeilingShader(gl, gl.VERTEX_SHADER, ceilingVertexShader);
  const fragmentShader = compileCeilingShader(gl, gl.FRAGMENT_SHADER, ceilingFragmentShader);
  const program = gl.createProgram();
  if (!vertexShader || !fragmentShader || !program) return emptyCeilingWebGlScene();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return emptyCeilingWebGlScene();
  gl.useProgram(program);
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);

  const positionLocation = gl.getAttribLocation(program, "a_position");
  const uvLocation = gl.getAttribLocation(program, "a_uv");
  const opacityLocation = gl.getUniformLocation(program, "u_opacity");
  const positionBuffer = gl.createBuffer();
  const uvBuffer = gl.createBuffer();
  if (!positionBuffer || !uvBuffer) return emptyCeilingWebGlScene();

  const gpuTextures = new Map<string, WebGLTexture>();
  const canUseNpotMipmaps = typeof WebGL2RenderingContext !== "undefined" && gl instanceof WebGL2RenderingContext;
  const anisotropy = gl.getExtension("EXT_texture_filter_anisotropic")
    ?? gl.getExtension("MOZ_EXT_texture_filter_anisotropic")
    ?? gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
  textures.forEach((source, id) => {
    const texture = gl.createTexture();
    if (!texture) return;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, canUseNpotMipmaps ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    if (canUseNpotMipmaps) gl.generateMipmap(gl.TEXTURE_2D);
    if (anisotropy) {
      const max = gl.getParameter(anisotropy.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
      gl.texParameterf(gl.TEXTURE_2D, anisotropy.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, max));
    }
    gpuTextures.set(id, texture);
  });

  const sampleQuad = (
    points: readonly [Point, Point, Point, Point],
    u: number,
    v: number,
  ) => {
    const near = interpolatePoint(points[0], points[1], u);
    const far = interpolatePoint(points[3], points[2], u);
    return interpolatePoint(near, far, v);
  };

  // Build a fresh, deterministic deck for every complete pass through the
  // submissions. Reusing one affine stride made the same palette positions
  // recur along the tunnel and read as deliberate red/pink/white lines.
  // A hashed deck per pass keeps usage balanced (every item appears once per
  // deck), breaks those colour bands, and stays identical on every Ventuz
  // renderer without consuming Math.random() or adding per-frame work.
  const sortPool = (pool: WallItem[], label: string) => [...pool].sort((left, right) => {
    const leftScore = hashString(`ceiling-${label}:${left.id}`);
    const rightScore = hashString(`ceiling-${label}:${right.id}`);
    if (leftScore !== rightScore) return leftScore - rightScore;
    return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
  });
  const photoItems = sortPool(items.filter((item) => item.hasImage), "photos");
  const textItems = sortPool(items.filter((item) => !item.hasImage), "text");
  const photoShare = photoItems.length / Math.max(1, items.length);
  const distributionOffset = (hashString(items.map((item) => item.id).join("|")) % 10_000) / 10_000;
  let photoIndex = 0;
  let textIndex = 0;
  const itemForSlot = (index: number) => {
    // A low-discrepancy sequence spreads portraits through every plane and
    // depth instead of allowing shuffled decks to cluster them into rows. It
    // looks irregular, remains balanced, and is deterministic on every Ventuz
    // renderer because it never consumes Math.random().
    const sequenceValue = (distributionOffset + index * .618033988749895) % 1;
    const usePhoto = photoItems.length > 0 && (textItems.length === 0 || sequenceValue < photoShare);
    if (usePhoto) {
      const item = photoItems[photoIndex % photoItems.length];
      photoIndex += 1;
      return item;
    }
    const item = textItems[textIndex % textItems.length] ?? photoItems[photoIndex % photoItems.length];
    textIndex += 1;
    return item;
  };

  const textItemForSlot = () => {
    const item = textItems[textIndex % Math.max(1, textItems.length)] ?? photoItems[photoIndex % Math.max(1, photoItems.length)];
    textIndex += 1;
    return item;
  };
  const slotAspect = (points: readonly [Point, Point, Point, Point]) => {
    const distance = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);
    const along = (distance(points[0], points[1]) + distance(points[3], points[2])) / 2;
    const across = (distance(points[0], points[3]) + distance(points[1], points[2])) / 2;
    return Math.max(.001, along / Math.max(.001, across));
  };
  const mergeSlotPoints = (
    nearSlot: TunnelSlot,
    farSlot: TunnelSlot,
  ): readonly [Point, Point, Point, Point] => [
    farSlot.points[0],
    farSlot.points[1],
    nearSlot.points[2],
    nearSlot.points[3],
  ];
  type CeilingPlacement = {
    slot: TunnelSlot;
    item: WallItem;
    farDepthIndex: number;
  };
  const placements: CeilingPlacement[] = [];
  const columns = new Map<string, TunnelSlot[]>();
  slots.forEach((slot) => {
    const key = `${slot.plane}:${slot.order}`;
    const column = columns.get(key) ?? [];
    column.push(slot);
    columns.set(key, column);
  });
  let assignmentIndex = 0;
  columns.forEach((column) => {
    const depthSlots = column.sort((left, right) => left.depthIndex - right.depthIndex);
    let cursor = 0;
    while (cursor < depthSlots.length) {
      const nearSlot = depthSlots[cursor];
      let item = itemForSlot(assignmentIndex);
      assignmentIndex += 1;
      if (!item) break;

      if (!item.hasImage) {
        placements.push({ slot: nearSlot, item, farDepthIndex: nearSlot.depthIndex });
        cursor += 1;
        continue;
      }

      const source = textures.get(item.id);
      const textureAspect = source ? source.width / Math.max(1, source.height) : .62;
      const portraitWidthScale = nearSlot.plane === "top" || nearSlot.plane === "bottom" ? .9 : .74;
      let span = 1;
      let mergedPoints = nearSlot.points;
      // Use the smallest number of original perspective belts that lets the
      // portrait retain its width and aspect ratio. The consumed belts are not
      // assigned another card in this column, so photographs can never stack
      // over neighbouring rows.
      while (span < 4 && cursor + span < depthSlots.length) {
        const candidateFarSlot = depthSlots[cursor + span];
        const candidatePoints = mergeSlotPoints(nearSlot, candidateFarSlot);
        mergedPoints = candidatePoints;
        span += 1;
        const requiredVScale = slotAspect(candidatePoints) * portraitWidthScale / Math.max(.001, textureAspect);
        if (requiredVScale <= .92) break;
      }
      if (span === 1 && cursor + 1 >= depthSlots.length && textItems.length > 0) {
        // A final one-row sliver cannot hold a portrait cleanly; keep that
        // innermost belt useful with copy instead of squeezing an image into it.
        item = textItemForSlot();
      }
      const farSlot = depthSlots[Math.min(depthSlots.length - 1, cursor + span - 1)];
      const placementSlot = item.hasImage
        ? { ...nearSlot, points: mergedPoints }
        : nearSlot;
      placements.push({ slot: placementSlot, item, farDepthIndex: farSlot.depthIndex });
      cursor += item.hasImage ? span : 1;
    }
  });

  const renderPlacements = placements
    .sort((left, right) => right.farDepthIndex - left.farDepthIndex)
    .flatMap(({ slot, item }) => {
    const texture = gpuTextures.get(item.id);
    if (!texture) return [];
    const source = textures.get(item.id);
    const isHorizontalPlane = slot.plane === "top" || slot.plane === "bottom";
    const portraitWidthScale = isHorizontalPlane ? .9 : .74;
    const fittedPoints = source
      ? fitCeilingTextureIntoSlot(
          slot.points,
          source.width / Math.max(1, source.height),
          item.hasImage,
          portraitWidthScale,
          isHorizontalPlane ? 1.2 : 1,
        )
      : slot.points;
    // The fit already preserves the artwork's real aspect ratio. Give the copy
    // cards the small size lift that was requested without changing the tunnel
    // projection, ring spacing, or portrait proportions.
    const renderedPoints = item.hasImage
      ? fittedPoints
      : scaleCeilingQuadAroundCenter(fittedPoints, isHorizontalPlane ? 1.14 : 1.04);
    // A single four-corner fan interpolates each half of an oblique card on a
    // different diagonal. That was the source of the visible "waves" through
    // Helvetica. A small mesh keeps the warp continuous; near rows get more
    // subdivisions while distant rows stay deliberately cheap.
    const subdivisions = slot.depthIndex <= 1 ? 8 : slot.depthIndex <= 3 ? 4 : 2;
    const vertices: number[] = [];
    const uvs: number[] = [];
    const append = (u: number, v: number) => {
      const point = sampleQuad(renderedPoints, u, v);
      const x = point.x * renderScaleX;
      const y = point.y * renderScaleY;
      vertices.push((x / canvas.width) * 2 - 1, 1 - (y / canvas.height) * 2);
      uvs.push(u, v);
    };
    for (let row = 0; row < subdivisions; row += 1) {
      for (let column = 0; column < subdivisions; column += 1) {
        const u0 = column / subdivisions;
        const u1 = (column + 1) / subdivisions;
        const v0 = row / subdivisions;
        const v1 = (row + 1) / subdivisions;
        append(u0, v0);
        append(u1, v0);
        append(u1, v1);
        append(u0, v0);
        append(u1, v1);
        append(u0, v1);
      }
    }
    return [{
      texture,
      depthIndex: slot.depthIndex,
      baseOpacity: slot.opacity,
      vertices: new Float32Array(vertices),
      uvs: new Float32Array(uvs),
    }];
  });

  const levelCount = Math.max(1, ...slots.map((slot) => slot.depthIndex + 1));
  let disposed = false;
  let lastOpacityKey = "";
  const renderAt = (elapsedSeconds: number) => {
    if (disposed || gl.isContextLost()) return;
    const levelOpacities = Array.from(
      { length: levelCount },
      (_, depthIndex) => ceilingLevelOpacityAtTime(elapsedSeconds, depthIndex, levelCount),
    );
    // During the long hold and dark pause the preserved framebuffer needs no
    // redraw at all. Quantising to 1/1000 also avoids duplicate GPU work when
    // Ventuz injects the same frame more than once.
    const opacityKey = levelOpacities.map((opacity) => opacity.toFixed(3)).join("|");
    if (opacityKey === lastOpacityKey) return;
    lastOpacityKey = opacityKey;
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(program);
    for (const placement of renderPlacements) {
      const levelOpacity = levelOpacities[placement.depthIndex] ?? 0;
      if (levelOpacity <= .001) continue;
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, placement.vertices, gl.STREAM_DRAW);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, placement.uvs, gl.STREAM_DRAW);
      gl.enableVertexAttribArray(uvLocation);
      gl.vertexAttribPointer(uvLocation, 2, gl.FLOAT, false, 0, 0);
      gl.bindTexture(gl.TEXTURE_2D, placement.texture);
      gl.uniform1f(opacityLocation, placement.baseOpacity * levelOpacity);
      gl.drawArrays(gl.TRIANGLES, 0, placement.vertices.length / 2);
    }
    gl.flush();
  };

  return {
    renderAt,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      gpuTextures.forEach((texture) => gl.deleteTexture(texture));
      gl.deleteBuffer(positionBuffer);
      gl.deleteBuffer(uvBuffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
    },
  };
};

function CeilingTunnelCanvas({ slots, items }: {
  slots: readonly TunnelSlot[];
  items: readonly WallItem[];
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<CeilingWebGlScene | null>(null);
  const localAnimationStartRef = useRef(performance.now());
  const slotsKey = slots.map((slot) => `${slot.plane}:${slot.depthIndex}:${slot.order}`).join("|");
  const itemsKey = items.map((item) => `${item.id}:${item.imageUrl ?? ""}:${item.message}`).join("|");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !items.length || !slots.length) return;
    let cancelled = false;
    const width = Math.max(1, Math.round(canvas.clientWidth));
    const height = Math.max(1, Math.round(canvas.clientHeight));
    let webGlScene = emptyCeilingWebGlScene();
    void (async () => {
      await document.fonts.ready;
      const uniqueItems = [...new Map(items.map((item) => [item.id, item])).values()];
      // Assign a balanced palette after sorting submissions by a stable hash.
      // The previous index cycle was balanced but its repeating order lined up
      // with the tunnel stride, creating obvious same-colour rows/diagonals.
      // Hash-ranking breaks that spatial pattern while the rank modulo keeps
      // every colour equally represented. It is deterministic across Ventuz
      // renderers and adds no work to the per-frame WebGL draw.
      const paletteByItemId = new Map(
        [...uniqueItems]
          .sort((left, right) => {
            const scoreDelta = hashString(`ceiling-palette:${left.id}`) - hashString(`ceiling-palette:${right.id}`);
            if (scoreDelta !== 0) return scoreDelta;
            return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
          })
          .map((item, rank) => [item.id, rank % cardColorPairs.length] as const),
      );
      const textures = new Map<string, HTMLCanvasElement>();
      // One source per submission. The 1.9x master is enough for the two near
      // rows; WebGL mipmaps automatically provide cheaper levels deeper inside
      // the tunnel.
      await Promise.all(uniqueItems.map(async (item) => {
        textures.set(item.id, await buildCeilingCardCanvas(item, paletteByItemId.get(item.id) ?? 0, 1.9));
      }));
      if (cancelled) return;
      // Keep the previous framebuffer visible while the replacement cards are
      // prepared. Resizing clears WebGL, so do it only immediately before the
      // completed set is drawn; the 30-minute change then reads as one clean cut
      // instead of a blank/loading flash.
      // The physical LED canvas is 8K, but forcing Chrome to retain a 207 MB 8K
      // RGBA framebuffer is unnecessary. This 4096-wide master remains sharp
      // while cutting persistent framebuffer cost by about 76%.
      const ceilingRenderWidth = 4096;
      canvas.width = ceilingRenderWidth;
      canvas.height = Math.round(ceilingRenderWidth * screenSpecs.ceiling.height / screenSpecs.ceiling.width);
      const renderScaleX = canvas.width / width;
      const renderScaleY = canvas.height / height;
      webGlScene = drawCeilingWebGl(
        canvas,
        textures,
        slots,
        items,
        renderScaleX,
        renderScaleY,
      );
      sceneRef.current = webGlScene;
      // The parent mounts the ceiling only after the shared seven-second wall
      // intro. Start its own timeline at zero so the existing belt-by-belt
      // reveal begins from a genuinely empty tunnel.
      webGlScene.renderAt(0);
    })();
    return () => {
      cancelled = true;
      if (sceneRef.current === webGlScene) sceneRef.current = null;
      webGlScene.dispose();
    };
  }, [itemsKey, slotsKey]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const applyFloating = (elapsedSeconds: number) => {
      // A single compositor transform gives the complete portal a restrained
      // camera-float without re-rasterising its many cards. The phase is based
      // on absolute time/frame, so skipped frames jump to the correct position
      // rather than accumulating drift.
      const x = Math.sin(elapsedSeconds * Math.PI / 8.5) * .055;
      const y = Math.sin(elapsedSeconds * Math.PI / 11 + 1.7) * .045;
      const rotation = Math.sin(elapsedSeconds * Math.PI / 14 + .4) * .012;
      canvas.style.transform = `translate3d(${x.toFixed(4)}%, ${y.toFixed(4)}%, 0) rotate(${rotation.toFixed(4)}deg) scale(1.0015)`;
      canvas.style.opacity = "1";
      sceneRef.current?.renderAt(elapsedSeconds);
    };

    const frameClock = (window as Window & {
      __wallFrameClock?: { enabled: () => boolean; frame: () => number; fps: () => number };
    }).__wallFrameClock;
    let lastInjectedFrameAt = Number.NEGATIVE_INFINITY;
    const unsubscribe = subscribeVentuzFrames((frame) => {
      lastInjectedFrameAt = performance.now();
      const fps = frameClock?.fps() ?? 50;
      applyFloating(ceilingAnimationSecondsSinceIntro(frame * 1_000 / fps));
    });

    let animationFrame = 0;
    const animate = () => {
      // Keep the cycle alive while a sync-enabled Ventuz page is waiting for
      // its first injected frame, and recover automatically if injection ever
      // pauses. Fresh injected frames remain the authoritative phase source.
      if (performance.now() - lastInjectedFrameAt > 1_000) {
        applyFloating((performance.now() - localAnimationStartRef.current) / 1_000);
      }
      animationFrame = window.requestAnimationFrame(animate);
    };
    animationFrame = window.requestAnimationFrame(animate);
    return () => {
      unsubscribe();
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="ceiling-tunnel-canvas"
      aria-hidden="true"
    />
  );
}

const aliases: Record<string, ScreenName> = {
  "1": "east",
  "2": "south",
  "3": "west",
  "4": "north",
  "5": "ceiling",
};

const paletteSequence = ["red", "red", "black", "red", "pink", "red"] as const;

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const messageScale = (message: string): "short" | "medium" | "long" => {
  if (message.length > 120) return "long";
  if (message.length > 62) return "medium";
  return "short";
};

const generatedBackground = (id: string): "pink" | "black" =>
  Number.parseInt(id.replaceAll("-", "").slice(-2), 16) % 2 ? "pink" : "black";

const deterministicOrder = <T,>(items: readonly T[], seed: string): T[] =>
  items
    .map((item, index) => ({ item, score: hashString(`${seed}:${index}`) }))
    .sort((left, right) => left.score - right.score)
    .map(({ item }) => item);

export const wallItemRotationWeight = (item: WallItem, now = Date.now()): number => {
  const publishedAt = Date.parse(item.publishedAt || item.createdAt) || 0;
  const ageMs = Math.max(0, now - publishedAt);
  // Approval recency has a 45-minute half-life. A newly approved ordinary
  // card is therefore selected roughly six times as often as a three-hour-old
  // equivalent, while the positive base weight keeps the complete archive in
  // rotation. Admin-featured cards retain their separate strong frequency
  // boost, and images receive only a light tie-break rather than dominating.
  const recency = 9 * 2 ** (-ageMs / (45 * 60_000));
  const featured = item.featured ? 10 : 0;
  const image = item.hasImage ? 1.5 : 0;
  const copy = Math.min(2, Math.floor(item.message.length / 70));
  return 1 + recency + featured + image + copy;
};

const deterministicWeightedWallItemOrder = (
  items: readonly WallItem[],
  seed: string,
  now: number,
): WallItem[] => {
  const weightingTime = Math.floor(now / 15_000) * 15_000;
  return items
    .map((item) => {
      // Exponential-race ordering produces a deterministic weighted draw for
      // each synchronized card generation without making every wall choose the
      // same newest portrait.
      const unit = (hashString(`${seed}:${item.id}`) + 1) / 4_294_967_297;
      return { item, score: -Math.log(unit) / wallItemRotationWeight(item, weightingTime) };
    })
    .sort((left, right) => left.score - right.score)
    .map(({ item }) => item);
};

const firstShowItems = (items: WallItem[], count: number, now: number): WallItem[] => {
  const publishedAt = (item: WallItem) => Date.parse(item.publishedAt || item.createdAt) || 0;
  const recent = [...items]
    .filter((item) => now - publishedAt(item) <= 5 * 60_000)
    .sort((left, right) => publishedAt(right) - publishedAt(left));
  const immediate = recent.filter((item) => now - publishedAt(item) <= 15_000).slice(0, count);
  const immediateIds = new Set(immediate.map((item) => item.id));
  const queue = recent.filter((item) => !immediateIds.has(item.id));
  const remaining = Math.max(0, count - immediate.length);
  if (!remaining || !queue.length) return immediate;
  const start = (Math.floor(now / 5_000) * remaining) % queue.length;
  const rotating = Array.from(
    { length: Math.min(remaining, queue.length) },
    (_, index) => queue[(start + index) % queue.length],
  );
  return [...immediate, ...rotating];
};

const synchronizedWallCards = (
  items: WallItem[],
  screen: Exclude<ScreenName, "ceiling">,
  frame: number,
  fps: number,
  now = Date.now(),
): LiveWallCard[] => {
  const activeFrame = frame - Math.round(fps * wallCardIntroDelayMs / 1_000);
  if (activeFrame < 0 || !items.length) return [];

  const count = Math.min(screenSpecs[screen].itemCount, items.length);
  const usedItems = new Set<string>();
  const usedPlacements = new Set<number>();
  const result: LiveWallCard[] = [];
  // wallSelection places the guaranteed first-show lane at the front of each
  // screen's candidate list. Limit priority rendering to that physical prefix;
  // newer replacement candidates must not displace a burst item before its
  // scheduled first appearance.
  const priorityItems = firstShowItems(items.slice(0, count), count, now);

  for (let slotIndex = 0; slotIndex < count; slotIndex += 1) {
    const slotSeed = hashString(`${wallSyncSeed}:${screen}:slot:${slotIndex}`);
    const introDelay = slotIndex === 0 ? Math.round(fps * .08) : Math.round(fps * (.18 + slotIndex * .16));
    const slotFrame = activeFrame - introDelay;
    if (slotFrame < 0) continue;

    // Every logical slot has a stable, different 20-35 second lifetime. The
    // generation is derived from the absolute injected frame, never from a
    // timer callback, so late joiners land on the same state immediately.
    const lifetimeSeconds = 20 + (slotSeed % 16);
    const lifetimeFrames = Math.max(1, Math.round(fps * lifetimeSeconds));
    const generation = Math.floor(slotFrame / lifetimeFrames);
    const localFrame = slotFrame % lifetimeFrames;
    const transitionFrames = Math.max(1, Math.round(fps * .8));
    const phase: LiveWallCard["phase"] = localFrame < transitionFrames
      ? "entering"
      : localFrame >= lifetimeFrames - transitionFrames
        ? "exiting"
        : "visible";

    const itemOrder = deterministicWeightedWallItemOrder(
      items,
      `${wallSyncSeed}:${screen}:items:${slotIndex}:${generation}`,
      now,
    );
    const priorityItem = priorityItems[slotIndex];
    const item = priorityItem && !usedItems.has(priorityItem.id)
      ? priorityItem
      : itemOrder.find((candidate) => !usedItems.has(candidate.id)) ?? itemOrder[0];
    if (!item) continue;
    usedItems.add(item.id);

    const placementOrder = deterministicOrder(
      collageSlots[screen].map((_, index) => index),
      `${wallSyncSeed}:${screen}:placements:${slotIndex}:${generation}`,
    );
    const placementIndex = placementOrder.find((candidate) => !usedPlacements.has(candidate)) ?? placementOrder[0];
    usedPlacements.add(placementIndex);
    result.push({ item, slotIndex, placementIndex, generation, phase });
  }
  return result;
};

interface Props {
  screenId: string;
}

type BackgroundScene = { key: string; color: string; image?: string; tone?: "blue" };

const backgroundSceneAt = (elapsedSeconds: number): BackgroundScene => {
  const time = ((elapsedSeconds % backgroundLoopSeconds) + backgroundLoopSeconds) % backgroundLoopSeconds;
  // One shared, absolute-time sequence drives every installation screen.
  // Black is intentionally the dominant editorial background. The two image
  // passages use the same six cached frames on the wall screens; the ceiling
  // suppresses the bitmap while retaining this exact black phase and timing.
  if (time < 12) return { key: "black-a", color: "#000000" };
  if (time < 15) return { key: "pink-a", color: "#FF52EC" };
  if (time < 18) {
    const index = Math.min(5, Math.floor((time - 15) / .5));
    return { key: `frames-a-${index}`, color: "#000000", image: backgroundFrames.blue[index], tone: "blue" };
  }
  if (time < 30) return { key: "black-b", color: "#000000" };
  if (time < 33) return { key: "pink-b", color: "#FF52EC" };
  if (time < 38) {
    const index = Math.min(5, Math.floor((time - 33) / (5 / 6)));
    return { key: `frames-b-${index}`, color: "#000000", image: backgroundFrames.blue[index], tone: "blue" };
  }
  return { key: "black-c", color: "#000000" };
};

function ScreenBackground({ screen, syncFrame, syncFps, syncEnabled }: {
  screen: ScreenName;
  syncFrame: number;
  syncFps: number;
  syncEnabled: boolean;
}) {
  const initialSeconds = syncEnabled ? syncFrame / syncFps : Date.now() / 1000;
  // The ceiling tunnel stays editorial black at every point in the shared
  // timeline. Wall screens keep the synchronized black/pink/blue-frame loop.
  const sceneAt = useCallback((elapsedSeconds: number): BackgroundScene => (
    screen === "ceiling"
      ? { key: "ceiling-black", color: "#000000" }
      : backgroundSceneAt(elapsedSeconds)
  ), [screen]);
  const [scene, setScene] = useState(() => sceneAt(initialSeconds));
  const sceneKeyRef = useRef(scene.key);

  useEffect(() => {
    const update = (elapsedSeconds: number) => {
      const next = sceneAt(elapsedSeconds);
      if (next.key === sceneKeyRef.current) return;
      sceneKeyRef.current = next.key;
      setScene(next);
    };
    if (syncEnabled) return subscribeVentuzFrames((frame) => update(frame / syncFps));
    const timer = window.setInterval(() => update(Date.now() / 1000), 100);
    return () => window.clearInterval(timer);
  }, [sceneAt, syncEnabled, syncFps]);

  return (
    <div className="wall-video-bg wall-frame-bg" data-screen={screen} style={{ backgroundColor: scene.color }} aria-hidden="true">
      {scene.image && screen !== "ceiling" && (
        <img
          className={`wall-frame-bg__image${scene.tone ? ` wall-frame-bg__image--${scene.tone}` : ""}`}
          src={scene.image}
          alt=""
          draggable={false}
        />
      )}
    </div>
  );
}

export function WallPage({ screenId }: Props) {
  const screen = (aliases[screenId] ?? screenId) as ScreenName;
  const spec = screenSpecs[screen];
  const frameClock = useVentuzFrameClock();
  const debug = new URLSearchParams(window.location.search).has("debug");
  const wallRef = useRef<HTMLElement>(null);
  const [layoutNonce] = useState(() => {
    if (frameClock.enabled) return hashString(wallSyncSeed);
    const requestedSeed = new URLSearchParams(window.location.search).get("layoutSeed");
    if (requestedSeed) return hashString(requestedSeed);
    const random = new Uint32Array(1);
    window.crypto.getRandomValues(random);
    return random[0] ?? Date.now();
  });
  const [snapshot, setSnapshot] = useState<WallSnapshot | null>(null);
  const [visible, setVisible] = useState<WallItem[]>([]);
  const [ceilingTimelineVisible, setCeilingTimelineVisible] = useState<WallItem[]>([]);
  const [liveWallCards, setLiveWallCards] = useState<LiveWallCard[]>([]);
  const [showWallChrome, setShowWallChrome] = useState(false);
  const [cardsEnabled, setCardsEnabled] = useState(false);
  const [qr, setQr] = useState("");
  const [paletteTick, setPaletteTick] = useState(() => Math.floor(Date.now() / 15_000));
  const [ceilingViewport, setCeilingViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  const ceilingTunnelSlots = useMemo(
    () => buildCeilingTunnelSlots(ceilingViewport.width, ceilingViewport.height),
    [ceilingViewport.height, ceilingViewport.width],
  );
  const reloadStorageKey = `madonna-wall-reload:${screenId}`;
  const reloadRevision = useRef<number | null>((() => {
    try {
      const stored = Number.parseInt(window.sessionStorage.getItem(reloadStorageKey) ?? "", 10);
      return Number.isFinite(stored) ? stored : null;
    } catch {
      return null;
    }
  })());
  const snapshotRef = useRef<WallSnapshot | null>(null);
  const ceilingSetTickRef = useRef<number | null>(null);
  const visibleRef = useRef<WallItem[]>([]);
  const availableWallItemsRef = useRef<WallItem[]>([]);
  const liveWallCardsRef = useRef<LiveWallCard[]>([]);
  const priorityWallItemIdsRef = useRef<string[]>([]);
  const seenWallItemIdsRef = useRef<Set<string>>(new Set());
  const collageRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const wall = wallRef.current;
    if (!wall || screen !== "ceiling") return;

    const angle = 21 * Math.PI / 180;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const sourceWidth = 8494;
    const sourceHeight = 6094;
    const masterWidth = 8000;
    const masterHeight = 6560;
    const translateX = sourceHeight * sine + 70;
    const translateY = -2242;

    const applyProjection = () => {
      const viewportWidth = wall.clientWidth;
      const viewportHeight = wall.clientHeight;
      if (!viewportWidth || !viewportHeight) return;
      wall.style.transform = `matrix(${(sourceWidth / masterWidth) * cosine}, ${(viewportHeight / masterHeight) * sine * (sourceWidth / viewportWidth)}, ${-(viewportWidth / masterWidth) * sine * (sourceHeight / viewportHeight)}, ${(sourceHeight / masterHeight) * cosine}, ${(viewportWidth / masterWidth) * translateX}, ${(viewportHeight / masterHeight) * translateY})`;
    };

    wall.style.transformOrigin = "0 0";
    document.documentElement.style.background = "#000";
    document.body.style.background = "#000";
    applyProjection();
    window.addEventListener("resize", applyProjection);
    return () => {
      window.removeEventListener("resize", applyProjection);
      wall.style.removeProperty("transform");
      wall.style.removeProperty("transform-origin");
    };
  }, [screen]);

  useEffect(() => {
    if (frameClock.enabled) return;
    const chromeTimer = window.setTimeout(() => setShowWallChrome(true), 2_000);
    const cardsTimer = window.setTimeout(() => setCardsEnabled(true), wallCardIntroDelayMs);
    return () => {
      window.clearTimeout(chromeTimer);
      window.clearTimeout(cardsTimer);
    };
  }, [frameClock.enabled, screen]);

  const commitVisible = useCallback((next: WallItem[]) => {
    const current = visibleRef.current;
    const unchanged = current.length === next.length && current.every((item, index) => {
      const candidate = next[index];
      return candidate
        && item.id === candidate.id
        && item.imageUrl === candidate.imageUrl
        && item.imageTreatment === candidate.imageTreatment
        && item.name === candidate.name
        && item.message === candidate.message;
    });
    if (unchanged) return;
    visibleRef.current = next;
    // Individual cards own their transitions. A document-level view transition
    // would fade the whole collage whenever the feed changes and briefly make
    // the screen look empty.
    setVisible(next);
  }, [screen]);

  useEffect(() => {
    const seenIds = seenWallItemIdsRef.current;
    if (screen !== "ceiling") {
      // On a newly opened renderer, seed the real physical slots from the
      // selector's visible prefix. On subsequent polls, queue every newly
      // assigned candidate so it can pre-empt a normal 20-35 second lifetime.
      const priorityCandidates = seenIds.size ? visible : visible.slice(0, spec.itemCount);
      for (const item of priorityCandidates) {
        if (!seenIds.has(item.id) && !priorityWallItemIdsRef.current.includes(item.id)) {
          priorityWallItemIdsRef.current.push(item.id);
        }
      }
    }
    visible.forEach((item) => seenIds.add(item.id));
    availableWallItemsRef.current = visible;
    if (screen === "ceiling" || !cardsEnabled || !snapshot) return;
    // A 15-second selection refresh only changes the candidates for future
    // cards. Cards already on screen must finish their own exit animation;
    // otherwise several disappear abruptly whenever the selected subset turns.
    // Only a real removal/disable in the complete server feed cuts a live card.
    const freshItems = new Map(snapshot.items.map((item) => [item.id, item]));
    setLiveWallCards((current) => {
      let changed = false;
      const next = current.flatMap((card) => {
        const freshItem = freshItems.get(card.item.id);
        if (!freshItem) {
          changed = true;
          return [];
        }
        if (freshItem.imageUrl !== card.item.imageUrl || freshItem.imageTreatment !== card.item.imageTreatment) {
          changed = true;
          return [{ ...card, item: freshItem }];
        }
        return [card];
      });
      return changed ? next : current;
    });
  }, [cardsEnabled, screen, snapshot, visible]);

  useEffect(() => {
    liveWallCardsRef.current = liveWallCards;
  }, [liveWallCards]);

  useEffect(() => {
    if (frameClock.enabled || screen === "ceiling" || !cardsEnabled) return;

    let cancelled = false;
    let generation = 0;
    let nextExitAllowedAt = 0;
    let priorityExitPending = false;
    const timers = new Set<number>();
    const lastItemBySlot = new Map<number, string>();
    const lastPlacementBySlot = new Map<number, number>();
    const recentPlacementsByItem = new Map<string, number[]>();
    const placementUseCount = new Map<number, number>();
    const recentItemIds: string[] = [];
    const slotCount = screenSpecs[screen].itemCount;
    const exitDuration = 1_600;
    const exitSpacing = screen === "south" ? 2_800 : 2_200;

    const later = (callback: () => void, delay: number) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (!cancelled) callback();
      }, delay);
      timers.add(timer);
    };

    const chooseItem = (slotIndex: number): WallItem | null => {
      const pool = availableWallItemsRef.current;
      if (!pool.length) return null;
      // An exiting card is already available for replacement. The previous
      // item is still excluded below while the pool has any alternative.
      const activeIds = new Set(
        liveWallCardsRef.current
          .filter((card) => card.phase !== "exiting")
          .map((card) => card.item.id),
      );
      const priorityIndex = priorityWallItemIdsRef.current.findIndex((id) =>
        pool.some((item) => item.id === id) && !activeIds.has(id)
      );
      if (priorityIndex >= 0) {
        const [priorityId] = priorityWallItemIdsRef.current.splice(priorityIndex, 1);
        const priorityItem = pool.find((item) => item.id === priorityId);
        if (priorityItem) {
          priorityExitPending = false;
          recentItemIds.push(priorityItem.id);
          return priorityItem;
        }
      }
      const previousId = lastItemBySlot.get(slotIndex);
      const recentIds = new Set(recentItemIds.slice(-Math.min(10, Math.max(2, pool.length - slotCount))));
      let candidates = pool.filter((item) =>
        !activeIds.has(item.id) && item.id !== previousId && !recentIds.has(item.id)
      );
      if (!candidates.length) candidates = pool.filter((item) => !activeIds.has(item.id));
      if (!candidates.length) return null;
      const weighted = candidates.map((item) => ({
        item,
        weight: wallItemRotationWeight(item),
      }));
      const totalWeight = weighted.reduce((sum, candidate) => sum + candidate.weight, 0);
      let draw = Math.random() * totalWeight;
      for (const candidate of weighted) {
        draw -= candidate.weight;
        if (draw <= 0) {
          recentItemIds.push(candidate.item.id);
          return candidate.item;
        }
      }
      const fallbackItem = weighted.at(-1)?.item ?? null;
      if (fallbackItem) recentItemIds.push(fallbackItem.id);
      return fallbackItem;
    };

    const choosePlacement = (slotIndex: number, item: WallItem): number => {
      const placements = collageSlots[screen];
      const used = new Set(liveWallCardsRef.current.map((card) => card.placementIndex));
      const previous = lastPlacementBySlot.get(slotIndex);
      const itemHistory = new Set(recentPlacementsByItem.get(item.id) ?? []);
      let candidates = placements.map((_, index) => index)
        .filter((index) => !used.has(index) && index !== previous && !itemHistory.has(index));
      if (!candidates.length) {
        candidates = placements.map((_, index) => index)
          .filter((index) => !used.has(index) && index !== previous);
      }
      if (!candidates.length) candidates = placements.map((_, index) => index).filter((index) => !used.has(index));
      if (!candidates.length) return slotIndex % placements.length;

      const approximateBounds = (placement: CollageSlot, hasImage: boolean) => ({
        left: placement.x - 2,
        right: placement.x + placement.width * (hasImage ? 1.8 : 1.7) + 2,
        top: placement.y - 2,
        bottom: placement.y + (hasImage ? 24 : 10),
      });
      const activeBounds = liveWallCardsRef.current
        .filter((card) => card.phase !== "exiting")
        .map((card) => approximateBounds(placements[card.placementIndex], card.item.hasImage));
      const collisionFree = candidates.filter((index) => {
        const candidate = approximateBounds(placements[index], item.hasImage);
        return activeBounds.every((active) =>
          candidate.right <= active.left
          || candidate.left >= active.right
          || candidate.bottom <= active.top
          || candidate.top >= active.bottom
        );
      });
      if (collisionFree.length) candidates = collisionFree;

      const activePlacements = liveWallCardsRef.current
        .filter((card) => card.phase !== "exiting")
        .map((card) => placements[card.placementIndex])
        .filter(Boolean);
      // Prefer the least-used available areas, but randomly choose between the
      // best spatial options. This keeps the whole canvas occupied without
      // resolving into the same deterministic set of edge positions.
      const minimumUse = Math.min(...candidates.map((index) => placementUseCount.get(index) ?? 0));
      const underusedCandidates = candidates.filter((index) => (placementUseCount.get(index) ?? 0) <= minimumUse + 1);
      if (!activePlacements.length) {
        return underusedCandidates[Math.floor(Math.random() * underusedCandidates.length)];
      }

      const ranked = underusedCandidates
        .map((index) => {
          const candidate = placements[index];
          const minimumDistance = Math.min(...activePlacements.map((active) =>
            Math.hypot(candidate.x - active.x, (candidate.y - active.y) * 1.7)
          ));
          return { index, score: minimumDistance + Math.random() * 8 };
        })
        .sort((left, right) => right.score - left.score);
      const topBand = ranked.slice(0, Math.max(1, Math.ceil(ranked.length / 3)));
      return topBand[Math.floor(Math.random() * topBand.length)].index;
    };

    const requestExit = (slotIndex: number, cardGeneration: number) => {
      const currentCard = liveWallCardsRef.current.find((candidate) =>
        candidate.slotIndex === slotIndex && candidate.generation === cardGeneration
      );
      if (!currentCard || currentCard.phase === "exiting") return;

      const now = Date.now();
      const activeCount = liveWallCardsRef.current.filter((candidate) => candidate.phase !== "exiting").length;
      const availableCount = Math.min(slotCount, availableWallItemsRef.current.length);
      const minimumActiveCards = Math.max(1, Math.ceil(availableCount * .6));
      const occupancyDelay = activeCount <= minimumActiveCards ? 1_250 : 0;
      const spacingDelay = Math.max(0, nextExitAllowedAt - now);
      const delay = Math.max(occupancyDelay, spacingDelay);
      if (delay > 0) {
        later(() => requestExit(slotIndex, cardGeneration), delay + 40 + Math.random() * 180);
        return;
      }

      nextExitAllowedAt = now + exitSpacing + Math.random() * 900;
      setLiveWallCards((current) => {
        const next = current.map((candidate) =>
          candidate.generation === cardGeneration ? { ...candidate, phase: "exiting" as const } : candidate
        );
        liveWallCardsRef.current = next;
        return next;
      });

      // Start the replacement while the previous card is still dissolving.
      // activateSlot removes only this logical slot's outgoing generation, so
      // the visible population never drops and there is no empty beat.
      later(() => activateSlot(slotIndex), 40 + Math.random() * 80);

      later(() => {
        setLiveWallCards((current) => {
          const next = current.filter((candidate) => candidate.generation !== cardGeneration);
          liveWallCardsRef.current = next;
          return next;
        });
      }, exitDuration);
    };

    const activateSlot = (slotIndex: number) => {
      const item = chooseItem(slotIndex);
      if (!item) {
        later(() => activateSlot(slotIndex), 350 + Math.random() * 550);
        return;
      }

      generation += 1;
      const cardGeneration = generation;
      const placementIndex = choosePlacement(slotIndex, item);
      const card: LiveWallCard = { item, slotIndex, placementIndex, generation: cardGeneration, phase: "entering" };
      lastItemBySlot.set(slotIndex, item.id);
      lastPlacementBySlot.set(slotIndex, placementIndex);
      const itemPlacementHistory = recentPlacementsByItem.get(item.id) ?? [];
      recentPlacementsByItem.set(item.id, [...itemPlacementHistory, placementIndex].slice(-3));
      placementUseCount.set(placementIndex, (placementUseCount.get(placementIndex) ?? 0) + 1);
      setLiveWallCards((current) => {
        // Keep the outgoing generation mounted behind the new one until its
        // blur-out finishes. This makes replacement continuous instead of
        // briefly exposing the background between two cards.
        const next = [
          ...current.filter((candidate) => candidate.slotIndex !== slotIndex || candidate.phase === "exiting"),
          card,
        ];
        liveWallCardsRef.current = next;
        return next;
      });

      later(() => {
        setLiveWallCards((current) => current.map((candidate) =>
          candidate.generation === cardGeneration ? { ...candidate, phase: "visible" } : candidate
        ));
      }, 1_000);

      // Every slot receives its own 20–35 second lifetime. Because both the
      // initial phase and lifetime are independent, cards never resolve into a
      // single synchronized reveal/clear cycle.
      const lifetime = 20_000 + Math.random() * 15_000;
      later(() => requestExit(slotIndex, cardGeneration), lifetime);
    };

    // The canvas deliberately starts empty. Independent offsets create a loose,
    // non-circular build instead of revealing the whole composition at once.
    const shuffledSlots = Array.from({ length: slotCount }, (_, index) => index)
      .sort(() => Math.random() - .5);
    shuffledSlots.forEach((slotIndex, order) => {
      // Guarantee the first card just after the seven-second intro, then fill
      // every other zone within 4.45s. No usable part of the canvas remains
      // empty for more than five seconds during the initial build.
      const initialPhase = order === 0 ? 80 : 160 + order * 105 + Math.random() * 180;
      later(() => activateSlot(slotIndex), initialPhase);
    });

    // A newly selected approved submission should not wait for a random
    // 20–35 second lifetime. It pre-empts one card on its assigned screen;
    // the global selector assigns it to only one wall, avoiding duplication.
    const priorityTimer = window.setInterval(() => {
      const hasEligiblePriority = priorityWallItemIdsRef.current.some((id) =>
        availableWallItemsRef.current.some((item) => item.id === id)
        && !liveWallCardsRef.current.some((card) => card.item.id === id)
      );
      if (!hasEligiblePriority || priorityExitPending) return;
      const candidate = liveWallCardsRef.current.find((card) => card.phase === "visible");
      if (!candidate) return;
      priorityExitPending = true;
      requestExit(candidate.slotIndex, candidate.generation);
    }, 600);

    // Defensive occupancy watchdog: a server-side disable/removal can remove a
    // live card outside its normal lifetime. Refill that logical slot within a
    // fraction of a second rather than waiting for its now-stale exit timer.
    const occupancyTimer = window.setInterval(() => {
      for (let slotIndex = 0; slotIndex < slotCount; slotIndex += 1) {
        const occupied = liveWallCardsRef.current.some((card) => card.slotIndex === slotIndex);
        if (!occupied) activateSlot(slotIndex);
      }
    }, 240);

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearInterval(priorityTimer);
      window.clearInterval(occupancyTimer);
      timers.clear();
      liveWallCardsRef.current = [];
      setLiveWallCards([]);
    };
  }, [cardsEnabled, frameClock.enabled, screen]);

  useEffect(() => {
    void QRCode.toDataURL(publicUrl, {
      width: 820,
      margin: 1,
      color: { dark: "#000000", light: "#FFFFFF" },
      errorCorrectionLevel: "H",
    }).then(setQr);
  }, []);

  useLayoutEffect(() => {
    const collage = collageRef.current;
    if (!collage || screen === "ceiling") return;

    const constrainStories = () => {
      const canvas = collage.getBoundingClientRect();
      const safeMargin = Math.max(8, canvas.width * .012);
      // This includes the maximum CSS float excursion as well as a visual gap,
      // so cards remain separate for the whole animation, not only at rest.
      const cardGap = Math.max(14, canvas.width * .014);
      const reservedAreas = Array.from(
        document.querySelectorAll<HTMLElement>(`.wall--${screen} [data-wall-reserved="true"]`),
      ).map((element) => {
        const rect = element.getBoundingClientRect();
        const isSouthCta = element.classList.contains("south-cta");
        const horizontalPadding = isSouthCta ? canvas.width * .032 : canvas.width * .008;
        const verticalPadding = isSouthCta ? canvas.height * .04 : canvas.height * .012;
        return {
          left: rect.left - horizontalPadding,
          right: rect.right + horizontalPadding,
          top: rect.top - verticalPadding,
          bottom: rect.bottom + verticalPadding,
        };
      });
      const stories = Array.from(collage.querySelectorAll<HTMLElement>(".wall-story"));
      const measureStory = (story: HTMLElement) => {
        const parts = Array.from(
          // Measure the actual painted pieces, not only the caption wrapper.
          // Name/copy blocks have their own editorial translations and can
          // extend beyond that wrapper; ignoring them was the source of the
          // apparently "impossible" text-card overlaps.
          story.querySelectorAll<HTMLElement>(
            ".wall-story__portrait, .wall-story__caption, .wall-story__name, .wall-story__copy, .wall-story__copy > p",
          ),
        );
        if (!parts.length) return null;
        const bounds = parts.map((part) => part.getBoundingClientRect());
        return {
          left: Math.min(...bounds.map((rect) => rect.left)),
          top: Math.min(...bounds.map((rect) => rect.top)),
          right: Math.max(...bounds.map((rect) => rect.right)),
          bottom: Math.max(...bounds.map((rect) => rect.bottom)),
        };
      };
      const overlaps = (
        a: { left: number; right: number; top: number; bottom: number },
        b: { left: number; right: number; top: number; bottom: number },
        gap = 0,
      ) => a.right + gap > b.left
        && a.left - gap < b.right
        && a.bottom + gap > b.top
        && a.top - gap < b.bottom;

      // Always solve from the authored slots. Measuring an already corrected
      // layout made successive ResizeObserver passes drift and occasionally
      // reintroduced collisions.
      stories.forEach((story) => {
        story.style.setProperty("--story-shift-x", "0px");
        story.style.setProperty("--story-shift-y", "0px");
        story.style.removeProperty("visibility");
      });

      const occupied: Array<{ left: number; right: number; top: number; bottom: number }> = [];
      const measuredStories = stories
        .map((story) => ({ story, bounds: measureStory(story) }))
        .filter((entry): entry is { story: HTMLElement; bounds: { left: number; right: number; top: number; bottom: number } } => Boolean(entry.bounds))
        // Place the largest/photo cards first. Small text cards can then fill
        // the remaining holes without pushing portraits into reserved areas.
        .sort((a, b) => {
          const aPhoto = a.story.classList.contains("wall-story--image") ? 1 : 0;
          const bPhoto = b.story.classList.contains("wall-story--image") ? 1 : 0;
          if (aPhoto !== bPhoto) return bPhoto - aPhoto;
          const aArea = (a.bounds.right - a.bounds.left) * (a.bounds.bottom - a.bounds.top);
          const bArea = (b.bounds.right - b.bounds.left) * (b.bounds.bottom - b.bounds.top);
          return bArea - aArea;
        });

      measuredStories.forEach(({ story, bounds }) => {
        const width = bounds.right - bounds.left;
        const height = bounds.bottom - bounds.top;
        const minLeft = canvas.left + safeMargin;
        const maxLeft = canvas.right - safeMargin - width;
        const minTop = canvas.top + safeMargin;
        const maxTop = canvas.bottom - safeMargin - height;
        const authoredLeft = Math.min(maxLeft, Math.max(minLeft, bounds.left));
        const authoredTop = Math.min(maxTop, Math.max(minTop, bounds.top));
        const seed = hashString(`${screen}:${story.dataset.submissionId ?? "story"}:${story.dataset.placementIndex ?? "0"}`);
        const stepX = Math.max(cardGap * 1.15, width * .16);
        const stepY = Math.max(cardGap * 1.15, height * .14);
        const candidates: Array<{ left: number; top: number; score: number }> = [];

        // First keep the editorially authored position when it is valid.
        candidates.push({ left: authoredLeft, top: authoredTop, score: 0 });
        // Then search the complete usable canvas. The tiny seeded score keeps
        // the result visually irregular while remaining deterministic across
        // all renderer machines.
        for (let top = minTop; top <= maxTop + 1; top += stepY) {
          for (let left = minLeft; left <= maxLeft + 1; left += stepX) {
            const distance = Math.hypot(left - authoredLeft, top - authoredTop);
            const noise = (hashString(`${seed}:${Math.round(left)}:${Math.round(top)}`) % 1000) / 1000;
            candidates.push({ left, top, score: distance + noise * Math.min(stepX, stepY) * .7 });
          }
        }
        candidates.sort((a, b) => a.score - b.score);

        const candidate = candidates.find((position) => {
          const rect = {
            left: position.left,
            right: position.left + width,
            top: position.top,
            bottom: position.top + height,
          };
          if (reservedAreas.some((reserved) => overlaps(rect, reserved, cardGap))) return false;
          return !occupied.some((other) => overlaps(rect, other, cardGap));
        });

        // Never render a dirty fallback. If the current composition is truly
        // full, the lowest-priority card stays hidden until the next rotation.
        if (!candidate) {
          story.style.setProperty("visibility", "hidden");
          return;
        }
        const shiftX = candidate.left - bounds.left;
        const shiftY = candidate.top - bounds.top;
        story.style.setProperty("--story-shift-x", `${shiftX}px`);
        story.style.setProperty("--story-shift-y", `${shiftY}px`);
        occupied.push({
          left: candidate.left,
          right: candidate.left + width,
          top: candidate.top,
          bottom: candidate.top + height,
        });
      });
    };

    constrainStories();
    const observer = new ResizeObserver(constrainStories);
    observer.observe(collage);
    collage.querySelectorAll<HTMLElement>(
      ".wall-story, .wall-story__portrait, .wall-story__caption, .wall-story__name, .wall-story__copy",
    ).forEach((part) => observer.observe(part));
    // Font decoding can finish after the first layout pass without resizing
    // the outer story box. Re-run once the branded Helvetica files are ready.
    void document.fonts?.ready.then(constrainStories);
    let mutationFrame = 0;
    const mutationObserver = new MutationObserver(() => {
      window.cancelAnimationFrame(mutationFrame);
      mutationFrame = window.requestAnimationFrame(constrainStories);
    });
    // Watch the entire stage rather than only the collage: the south/north CTA
    // is a sibling and must reserve its space the instant it appears.
    mutationObserver.observe(collage.parentElement ?? collage, { childList: true, subtree: true });
    window.addEventListener("resize", constrainStories);
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      window.cancelAnimationFrame(mutationFrame);
      window.removeEventListener("resize", constrainStories);
    };
  }, [
    qr,
    screen,
    visible,
    liveWallCards,
    showWallChrome,
    snapshot?.revision,
  ]);

  useLayoutEffect(() => {
    const collage = collageRef.current;
    if (!collage || screen !== "ceiling") return;
    const updateSize = () => {
      // Use the untransformed layout box. The ceiling output matrix is applied
      // to the complete wall afterwards and must not feed back into tunnel geometry.
      const width = collage.clientWidth;
      const height = collage.clientHeight;
      setCeilingViewport((current) => (
        Math.abs(current.width - width) < .5 && Math.abs(current.height - height) < .5
          ? current
          : { width, height }
      ));
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(collage);
    return () => observer.disconnect();
  }, [screen]);

  const choose = useCallback((next: WallSnapshot, tick = Math.floor(Date.now() / 15_000)) => {
    commitVisible(selectWallItems(next.items, screen as WallScreenName, tick));
  }, [commitVisible, screen]);

  useEffect(() => {
    let cancelled = false;
    let loading = false;
    const load = async () => {
      if (loading) return;
      loading = true;
      try {
        const next = await getWall(screenId);
        if (cancelled) return;
        if (reloadRevision.current !== null && next.reloadRevision > reloadRevision.current) {
          reloadRevision.current = next.reloadRevision;
          try {
            window.sessionStorage.setItem(reloadStorageKey, String(next.reloadRevision));
          } catch {
            // A reload still works when session storage is unavailable.
          }
          window.location.reload();
          return;
        }
        if (reloadRevision.current === null || next.reloadRevision > reloadRevision.current) {
          reloadRevision.current = next.reloadRevision;
          try {
            window.sessionStorage.setItem(reloadStorageKey, String(next.reloadRevision));
          } catch {
            // Keep polling with the in-memory revision when storage is unavailable.
          }
        }
        const previous = snapshotRef.current;
        snapshotRef.current = next;
        setSnapshot(next);
        const current = visibleRef.current;
        if (!next.items.length) {
          commitVisible([]);
        } else {
          const byId = new Map(next.items.map((item) => [item.id, item]));
          const previousIds = new Set(previous?.items.map((item) => item.id) ?? []);
          const safetyStateChanged =
            previous?.hidePictures !== next.hidePictures ||
            previous?.hideAllContent !== next.hideAllContent ||
            previous?.showId !== next.showId;
          const hasRemovedItem = current.some((item) => !byId.has(item.id));
          const hasNewPublishedItem = next.items.some((item) => !previousIds.has(item.id));
          if (!current.length || safetyStateChanged || hasRemovedItem || hasNewPublishedItem) {
            commitVisible(selectWallItems(next.items, screen as WallScreenName, Math.floor(Date.now() / 15_000)));
          } else {
            commitVisible(current.map((item) => byId.get(item.id) ?? item));
          }
        }
      } catch {
        // Preserve the last good render during short network interruptions.
      } finally {
        loading = false;
      }
    };
    void load();
    const poll = window.setInterval(load, 2_000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [commitVisible, reloadStorageKey, screen, screenId]);

  useEffect(() => {
    if (frameClock.enabled) return;
    let previousTick = Math.floor(Date.now() / 15_000);
    const rotation = window.setInterval(() => {
      const nextTick = Math.floor(Date.now() / 15_000);
      if (nextTick !== previousTick && snapshotRef.current) {
        previousTick = nextTick;
        choose(snapshotRef.current, nextTick);
      }
    }, 1_000);
    return () => window.clearInterval(rotation);
  }, [choose, frameClock.enabled]);

  useEffect(() => {
    if (frameClock.enabled) return;
    const updatePalette = () => setPaletteTick(Math.floor(Date.now() / 15_000));
    const paletteTimer = window.setInterval(updatePalette, 1_000);
    return () => window.clearInterval(paletteTimer);
  }, [frameClock.enabled]);

  const synchronizedTick = Math.floor(frameClock.frame / Math.max(1, Math.round(frameClock.fps * 15)));
  const effectiveVisible = useMemo(() => (
    frameClock.enabled && snapshot
      ? selectWallItems(snapshot.items, screen as WallScreenName, synchronizedTick, Date.now())
      : visible
  ), [frameClock.elapsedMs, frameClock.enabled, screen, snapshot, synchronizedTick, visible]);
  const effectiveShowWallChrome = frameClock.enabled ? frameClock.elapsedMs >= 2_000 : showWallChrome;
  // Every live surface observes the same approved seven-second opening beat.
  // On the ceiling, mounting at this boundary also starts its own animation at
  // zero, preserving the outer-edge-to-aperture reveal instead of cutting into
  // an arbitrary point of the repeating cycle.
  const effectiveCardsEnabled = frameClock.enabled
    ? frameClock.elapsedMs >= wallCardIntroDelayMs
    : cardsEnabled;
  const timelineVisible = effectiveCardsEnabled ? effectiveVisible : [];
  const ceilingSetTick = frameClock.enabled
    ? Math.floor(frameClock.elapsedMs / ceilingCardSetDurationMs)
    : Math.floor(Date.now() / ceilingCardSetDurationMs);

  useEffect(() => {
    if (screen !== "ceiling" || !effectiveCardsEnabled || !snapshot) {
      ceilingSetTickRef.current = null;
      setCeilingTimelineVisible((current) => current.length ? [] : current);
      return;
    }

    // The ceiling is intentionally a static, inexpensive composition. Freeze
    // the selected submissions for a complete 30-minute block even while the
    // wall feed keeps polling. At the shared block boundary every renderer
    // selects the next deterministic set and replaces the canvas once, with no
    // crossfade or second retained framebuffer.
    if (ceilingSetTickRef.current === ceilingSetTick) return;
    const nextItems = selectWallItems(
      snapshot.items,
      "ceiling",
      ceilingSetTick,
      ceilingSetTick * ceilingCardSetDurationMs,
    );
    if (!nextItems.length) return;
    ceilingSetTickRef.current = ceilingSetTick;
    setCeilingTimelineVisible(nextItems);
  }, [ceilingSetTick, effectiveCardsEnabled, screen, snapshot]);
  const synchronizedCards = useMemo(() => (
    frameClock.enabled && screen !== "ceiling"
      ? synchronizedWallCards(timelineVisible, screen, frameClock.frame, frameClock.fps, Date.now())
      : liveWallCards
  ), [frameClock.enabled, frameClock.fps, frameClock.frame, liveWallCards, screen, timelineVisible]);
  const effectivePaletteTick = frameClock.enabled ? synchronizedTick : paletteTick;

  const requestedPalette = new URLSearchParams(window.location.search).get("theme");
  const palette = requestedPalette === "red" || requestedPalette === "black" || requestedPalette === "pink"
    ? requestedPalette
    : paletteSequence[effectivePaletteTick % paletteSequence.length];
  if (!snapshot) {
    return (
      <main ref={wallRef} className={`wall wall--live wall--${screen}`}>
        <ScreenBackground screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
        <ScreenStrobe screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
      </main>
    );
  }

  const fallback = ["idle", "paused", "fallback", "ended"].includes(snapshot.phase);

  if (snapshot?.phase === "qr") {
    return (
      <main ref={wallRef} className={`wall wall--qr wall--${screen}`}>
        <ScreenBackground screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
        <div className="wall-qr__copy">
          <span>Madonna / London</span>
          <h1>Meet me on<br />the dance floor.</h1>
          <p>Scan. Say it. See it.</p>
        </div>
        {qr && <img className="wall-qr__code" src={qr} alt="Scan to send Madonna a message" />}
        <ScreenStrobe screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
        {debug && <strong className="wall__screen-id">{screen.toUpperCase()} · {spec.width}×{spec.height}</strong>}
      </main>
    );
  }

  if (fallback) {
    return (
      <main ref={wallRef} className={`wall wall--fallback wall--${screen}`}>
        <ScreenBackground screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
        <div className="wall-fallback__word">MADONNA</div>
        <div className="wall-fallback__line">C’MON MEET ME ON THE DANCEFLOOR</div>
        <ScreenStrobe screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
        {debug && <strong className="wall__screen-id">{screen.toUpperCase()} · {spec.width}×{spec.height}</strong>}
      </main>
    );
  }

  if (screen === "ceiling") {
    return (
      <main ref={wallRef} className={`wall wall--live wall--ceiling wall--theme-${palette}`}>
        <ScreenBackground screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
        <div ref={collageRef} className="wall-collage ceiling-tunnel ceiling-tunnel--canvas">
          {ceilingTimelineVisible.length > 0 && (
            <CeilingTunnelCanvas
              slots={ceilingTunnelSlots}
              items={ceilingTimelineVisible}
            />
          )}
        </div>
        <ScreenStrobe screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
        {debug && <strong className="wall__screen-id">CEILING · {spec.width}×{spec.height}</strong>}
      </main>
    );
  }

  const stories = synchronizedCards.map((card, index) => ({
    item: card.item,
    index: card.placementIndex ?? index,
    displayPhoto: Boolean(card.item.hasImage && card.item.imageUrl),
    generation: card.generation ?? 0,
    phase: card.phase ?? "visible",
  }));

  return (
    <main ref={wallRef} className={`wall wall--live wall--${screen} wall--theme-${palette}`}>
      <ScreenBackground screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
      <div ref={collageRef} className="wall-collage">
        {stories.map(({ item, index, displayPhoto, generation = 0, phase = "visible" }) => {
          const slots = collageSlots[screen];
          const slotIndex = index % slots.length;
          const slot = slots[slotIndex];
          const seed = hashString(`${screen}:${item.id}:${layoutNonce}:${generation}`);
          const jitterScale = 1;
          const xJitter = ((seed % 9) - 4) * .36 * jitterScale;
          const yJitter = (((seed >>> 4) % 9) - 4) * .48 * jitterScale;
          const widthScale = .94 + ((seed >>> 8) % 8) * .018;
          // The South wall is physically much larger than its browser preview.
          // Keep the permanent QR/CTA at its approved size, while reducing only
          // the surrounding guest cards to a more appropriate real-world scale.
          const screenCardScale = screen === "south" ? .88 : 1;
          const tiltJitter = (((seed >>> 12) % 9) - 4) * .18 * jitterScale;
          const tone = (seed + index) % 3;
          const colorSeed = hashString(`${screen}:${item.id}:${index}:${layoutNonce}:color`);
          const storyColorIndex = colorSeed % cardColorPairs.length;
          const copyColorIndex = (storyColorIndex + 1 + ((colorSeed >>> 5) % (cardColorPairs.length - 1))) % cardColorPairs.length;
          const nameColorIndex = (copyColorIndex + 1 + ((colorSeed >>> 9) % (cardColorPairs.length - 1))) % cardColorPairs.length;
          const storyColors = cardColorPairs[storyColorIndex];
          const copyColors = cardColorPairs[copyColorIndex];
          const nameColors = cardColorPairs[nameColorIndex];
          const photoBackground = item.hasImage ? generatedBackground(item.id) : null;
          const nameStyle = "tag";
          const guestTypography = "body";
          const guestCopyTone = (seed >>> 15) % 3 === 0 ? "alt" : "base";
          const lockupDirection = (hashString(`${item.id}:${generation}:lockup`) & 1) === 0 ? -1 : 1;
          const textNameOffset = lockupDirection * (6 + ((seed >>> 20) % 5));
          const textCopyOffset = -lockupDirection * (10 + ((seed >>> 24) % 7));
          // Name and message are one editorial lockup. Their variants must move
          // together so the two touching surfaces can never resolve to one color.
          const guestNameTone = guestCopyTone;
          const randomColorStyle = {
            "--story-bg": storyColors.background,
            "--story-ink": storyColors.ink,
            "--guest-copy-bg": copyColors.background,
            "--guest-copy-ink": copyColors.ink,
            "--guest-name-bg": nameColors.background,
            "--guest-name-ink": nameColors.ink,
          } as CSSProperties;
          const floatDirectionX = ((seed >>> 7) & 1) ? 1 : -1;
          const floatDirectionY = ((seed >>> 8) & 1) ? 1 : -1;
          const floatX = floatDirectionX * (.08 + ((seed >>> 9) % 10) / 100);
          const floatY = floatDirectionY * (.12 + ((seed >>> 13) % 12) / 100);
          const style: CSSProperties = ({
            ...randomColorStyle,
            "--story-x": `${slot.x + xJitter}%`,
            "--story-y": `${slot.y + yJitter}%`,
            // Portraits are the visual anchor of a photo card. Give the image
            // substantially more area without inflating its caption type.
            "--story-width": `${slot.width * widthScale * (displayPhoto ? 1.65 : 1) * screenCardScale}%`,
            // Keep text-only cards responsive, but visually subordinate to the
            // permanent screen CTA at both 4K and native output resolutions.
            "--story-text-width": `${slot.width * widthScale * 1.6 * screenCardScale}%`,
            "--story-tilt": `${slot.tilt + tiltJitter}deg`,
            "--story-photo-tilt": `${slot.tilt + tiltJitter}deg`,
            "--story-copy-tilt": `${slot.tilt + tiltJitter + (((seed >>> 16) % 7) - 3) * .22}deg`,
            "--story-name-tilt": `${slot.tilt + tiltJitter + (((seed >>> 19) % 9) - 4) * .28}deg`,
            "--story-copy-x": `${3 + ((seed >>> 22) % 7)}%`,
            "--story-name-x": `${-4 + ((seed >>> 25) % 13)}%`,
            "--story-copy-offset": displayPhoto
              ? `${(slot.x > 65 ? -1 : 1) * (-2 + ((seed >>> 22) % 7))}cqw`
              : `${textCopyOffset}cqw`,
            "--story-name-offset": displayPhoto
              ? `${(slot.x > 65 ? -1 : 1) * (-4 + ((seed >>> 25) % 9))}cqw`
              : `${textNameOffset}cqw`,
            "--story-delay": `${index * 95}ms`,
            "--story-z": 10 + index,
            "--portrait-scale": 1.015 + ((seed >>> 20) % 5) * 0.008,
            "--story-float-x": `${floatX}vw`,
            "--story-float-y": `${floatY}vh`,
            "--story-float-x-alt": `${floatX * -.55}vw`,
            "--story-float-y-alt": `${floatY * -.45}vh`,
            "--story-float-duration": `${7.5 + ((seed >>> 17) % 60) / 10}s`,
            "--story-float-delay": `${-((seed >>> 23) % 70) / 10}s`,
          } as CSSProperties);

          const portraitContent = displayPhoto && item.imageUrl ? (
            <div className="wall-story__portrait">
              <img src={item.imageUrl} alt="" />
            </div>
          ) : null;
          const copyContent = (
            <div className="wall-story__copy">
              <p>{item.message}</p>
            </div>
          );
          const nameContent = (
            <div className={`wall-story__name wall-story__name--${nameStyle}${item.name.length > 16 ? " wall-story__name--long" : ""}`}>
              {item.name}
            </div>
          );
          const storyContent = (
            <>
              {portraitContent}
              <div className="wall-story__caption">
                {nameContent}
                {copyContent}
              </div>
            </>
          );

          return (
            <article
              className={`wall-story wall-story--phase-${phase} wall-story--${displayPhoto ? "image" : "text"}${item.imageTreatment === "temporary-flash" ? " wall-story--temporary-flash" : ""} wall-story--guest-card wall-story--guest-font-${guestTypography} wall-story--guest-copy-${guestCopyTone} wall-story--guest-name-${guestNameTone}${slot.x > 65 ? " wall-story--caption-left" : ""}${photoBackground ? ` wall-story--bg-${photoBackground}` : ""} wall-story--caption-below wall-story--tone-${tone} wall-story--copy-${messageScale(item.message)}`}
              data-submission-id={item.id}
              data-placement-index={index}
              data-card-phase={phase}
              key={`${item.id}:wall:${index}:${generation}`}
              style={style}
            >
              <div className="wall-story__float">{storyContent}</div>
            </article>
          );
        })}
      </div>
      {effectiveShowWallChrome && screen === "south" && (
        <div className="south-cta" data-wall-reserved="true" aria-label="Join the Madonna celebration">
          <div className="south-cta__copy south-cta__copy--scan" aria-hidden="true">
            <span className="south-cta__line south-cta__line--black">Scan the QR code</span>
            <span className="south-cta__line south-cta__line--pink">to join the celebration</span>
          </div>
          <div className="wall-live__qr south-cta__qr">
            {qr && <img src={qr} alt="Scan to send Madonna a message" />}
          </div>
          <div className="south-cta__copy south-cta__copy--send" aria-hidden="true">
            <span className="south-cta__line south-cta__line--pink">Send your confessions</span>
            <span className="south-cta__line south-cta__line--black">and birthday wishes</span>
            <span className="south-cta__line south-cta__line--pink">for Madonna</span>
          </div>
        </div>
      )}
      {effectiveShowWallChrome && screen === "south" && (
        <div className="south-safe-zones" aria-hidden="true">
          <div className="south-safe-zone south-safe-zone--left" data-wall-reserved="true"><span>Safe zone</span></div>
          <div className="south-safe-zone south-safe-zone--right" data-wall-reserved="true"><span>Safe zone</span></div>
        </div>
      )}
      {effectiveShowWallChrome && screen === "north" && (
        <div className="north-cta" data-wall-reserved="true" aria-label="Scan the QR code and join the celebration">
          <div className="north-cta__qr">
            {qr && <img src={qr} alt="Scan to send Madonna a message" />}
          </div>
          <div className="north-cta__copy" aria-hidden="true">
            <span>Scan the QR code</span>
            <span>and join the celebration</span>
          </div>
        </div>
      )}
      {effectiveCardsEnabled && !effectiveVisible.length && <div className="wall-live__empty">THE DANCE FLOOR IS OPEN</div>}
      {effectiveShowWallChrome && (screen === "east" || screen === "west" || screen === "north") && (
        <div className="wall__ticker" data-wall-reserved="true">
          {wallTickerText(screen)}
        </div>
      )}
      <ScreenStrobe screen={screen} syncFrame={frameClock.frame} syncFps={frameClock.fps} syncEnabled={frameClock.enabled} />
      {debug && frameClock.enabled && (
        <output className="wall__sync-debug" data-frame={frameClock.frame} data-epoch={synchronizedTick}>
          SYNC · F{frameClock.frame} · E{synchronizedTick}
        </output>
      )}
      {debug && <strong className="wall__screen-id">{screen.toUpperCase()} · {spec.width}×{spec.height}</strong>}
    </main>
  );
}
